import React from 'react';
import styled from 'styled-components';
import { ProjectContainer, ProjectTitle, ProjectInfo } from './styles';
import { useLanguage } from '../../contexts/LanguageContext';

const ProjectDescription = styled.div`
  margin-bottom: 30px;
  
  ul.list {
    list-style: none;
    padding: 0;
    
    li {
      margin-bottom: 15px;
      line-height: 24px;
      position: relative;
      padding-left: 20px;
      
      &:before {
        content: '•';
        color: #F34452;
        position: absolute;
        left: 0;
        font-weight: bold;
      }
    }
  }
`;

const ProjectWrapper = styled.div`
  margin-bottom: 40px;
  
  h4 {
    font-size: 20px;
    color: #333;
    margin: 0 0 20px 0;
    font-weight: 600;
  }
  
  img {
    width: 100%;
    max-width: 600px;
    height: auto;
    margin-bottom: 20px;
    border: 1px solid #eee;
  }
  
  iframe {
    max-width: 100%;
    margin-bottom: 20px;
  }
  
  .flex {
    display: flex;
    gap: 15px;
    margin: 20px 0;
    
    img {
      flex: 1;
      max-width: 300px;
    }
  }
  
  .seeDemo {
    text-align: center;
    margin: 20px 0;
    
    a {
      background: #F34452;
      color: #FFF;
      padding: 12px 30px;
      text-decoration: none;
      text-transform: uppercase;
      font-weight: 600;
      border-radius: 3px;
      transition: all 0.3s;
      
      &:hover {
        background: #d63946;
      }
    }
  }
  
  ul {
    list-style: none;
    padding: 0;
    
    li {
      margin: 10px 0;
      
      a {
        color: #F34452;
        text-decoration: none;
        
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
  
  ul.list-inline {
    display: flex;
    gap: 20px;
    
    li {
      b {
        color: #333;
      }
    }
  }
`;

interface ProjectJetProps {
}

const ProjectJet: React.FC<ProjectJetProps> = () => {
  const { t } = useLanguage();
  
  return (
    <ProjectContainer>
      <ProjectTitle>
        <h2>{t('jet.title')}</h2>
        <p className="tags">{t('jet.role')}</p>
      </ProjectTitle>
      
      <ProjectDescription>
        <ul className="list">
          <li>{t('jet.bullet1')}</li>
          <li>{t('jet.bullet2')}</li>
          <li>{t('jet.bullet3')}</li>
          <li>{t('jet.bullet4')}</li>
          <li>{t('jet.bullet5')}</li>
          <li>{t('jet.bullet6')}</li>
          <li>{t('jet.bullet7')}</li>
          <li>{t('jet.bullet8')}</li>
          <li>{t('jet.bullet9')}</li>
        </ul>
      </ProjectDescription>
      
      <ProjectInfo>
        <p><b>{t('jet.roleLabel')}</b> {t('jet.myRole')}</p>
        <p><b>{t('jet.skillsLabel')}</b> {t('jet.skillsUtilized')}</p>
      </ProjectInfo>

      <ProjectWrapper>
        <h4>{t('jet.guestCheckout')}</h4>
        <img loading="lazy" decoding="async" src="/images/projects/jet-1.gif" className="img-responsive gif" alt="jet guest checkout"/>
        <div className="seeDemo">
          <a href="https://jet.com" target="_blank" rel="noopener noreferrer">{t('jet.seeMoreWebsite')}</a>
        </div>
      </ProjectWrapper>

      <ProjectWrapper>
        <h4>{t('jet.meetupTitle')}</h4>
        <p className="tags">{t('jet.meetupSubtitle')}</p>
        <iframe 
          width="560" 
          height="315" 
          src="https://www.youtube.com/embed/tO1jsPOogFQ" 
          style={{ border: 0 }} 
          allow="autoplay; encrypted-media" 
          allowFullScreen
          title="Jet Meetup Presentation - Front End Testing at Jet.com"
        ></iframe>
        <div className="seeDemo">
          <a href="https://github.com/YuanyuanMaggie/meetup/blob/master/Testing.pptx" target="_blank" rel="noopener noreferrer">{t('jet.seeSlide')}</a>
        </div>
      </ProjectWrapper>

      <ProjectWrapper>
        <h4>{t('jet.hackathon1Title')}</h4>
        <p className="tags">{t('jet.hackathon1Subtitle')}</p>
        <iframe 
          src="https://player.vimeo.com/video/256215412" 
          width="640" 
          height="360" 
          style={{ border: 0 }} 
          allowFullScreen
          title="Jetness Hackathon Demo - Jet Innovation Hackathon"
        ></iframe>
        <p><a href="https://vimeo.com/256215412">{t('jet.hackathon1Demo')}</a> {t('jet.hackathon1Author')} <a href="https://vimeo.com/user49854291">Yuanyuan Li</a> {t('jet.hackathon1Platform')} <a href="https://vimeo.com">Vimeo</a>.</p>
        <ul>
          <li>{t('jet.hackathon1Github')} <a href="https://github.com/YuanyuanMaggie/jetness">https://github.com/YuanyuanMaggie/jetness</a></li>
        </ul>
        <div className="flex">
          <img loading="lazy" decoding="async" src="/images/portfolio/jetness1-min.webp" alt="Jetness 1" className="img-thumbnai img-responsive"/>
          <img loading="lazy" decoding="async" src="/images/portfolio/jetness2-min.webp" alt="Jetness 2" className="img-thumbnai img-responsive"/>
        </div>
        <div className="seeDemo">
          <a href="https://jetness.herokuapp.com/" target="_blank" rel="noopener noreferrer">{t('jet.seeDemoWebsite')}</a>
        </div>
      </ProjectWrapper>

      <ProjectWrapper>
        <h4>{t('jet.hackathon2Title')}</h4>
        <p className="tags">{t('jet.hackathon2Subtitle')}</p>
        <iframe 
          src="https://player.vimeo.com/video/256216284" 
          width="640" 
          height="311" 
          style={{ border: 0 }} 
          allowFullScreen
          title="Cooking with Jet - Jet Innovation Hackathon 2 Demo"
        ></iframe>
        <p>
          <a href="https://vimeo.com/256216284">{t('jet.hackathon2Demo')}</a> {t('jet.hackathon1Author')} <a href="https://vimeo.com/user49854291">Yuanyuan Li</a> {t('jet.hackathon1Platform')} <a href="https://vimeo.com">Vimeo</a>.
        </p>
      </ProjectWrapper>

      <ProjectWrapper>
        <ul className="list-inline">
          <li><b>{t('jet.dateLabel')}</b> {t('jet.dateRange')}</li>
        </ul>
      </ProjectWrapper>
    </ProjectContainer>
  );
};

export default ProjectJet;